//Stores
import { get } from 'svelte/store';
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { user_data } from 'src/stores/account/user';
//Helpers
import {
	getDevicesWithItsChannels,
	is_gen_z,
	isCameraDevice,
	isWallDisplay,
	isXMODDevice,
	real_id,
} from './interface_helpers';
import { deleteVirtualChildren } from './virtual_helpers/virtual_components_helpers';
import { deletePeripheralChildren } from './addons/addon_device_helpers';
import { requestSaveGroup } from 'src/components/Lists/list-helpers';
import { removeCamerasFromCloudStorageSlots } from './camera_cloud_storage_helpers';
//Services
import { postReq, shendure } from '../services/http';
import { deleteSharedDevice as deleteSharedDeviceHouseMgmt } from 'src/services/iotu/house-management';
import { format } from 'svelte-i18n';

//Design system components
import { toast } from '@Shelly/shelly-design-system';
//Types
import type { Device } from '../types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import { removeWallDisplayComponents } from './wall_display_extract/extracting_helpers';
import { removeXMODComponents } from './xmod_extract/extracting_helpers';

let _format = get(format);

export async function deleteDevice(id: string) {
	let res_cloud: SettingRequest = undefined;
	let _id = real_id(id);
	const device = get(devices)[id];
	const isZendure = device.gen === 'GXP';

	let url = 'device/delete';
	if (device.gen == 'GBLE') {
		url = url + '_ble';
	}
	if (is_gen_z(device)) {
		const preparedData = {
			id: device.id,
			dsk: device.dsk,
			a: 'nl_del_n', //delete node
		};
		const fakeId = !device.id.substring(device.id.length - 4, device.id.length - 1).startsWith('N');
		res_cloud = await postReq(`/fast/device/zw`, {
			...preparedData,
		});
		if (typeof res_cloud?.errors?.wrong_device_id !== 'undefined' || fakeId || !res_cloud?.isok) {
			const res_interface = await deleteAllChannelsInterfaceData(device, 'deleted');

			if (res_interface.isok) {
				// clear device status and settings from stores.
				devices_status.remove(_id);
				devices_settings.remove(_id);
			}
		}
	} else {
		res_cloud = await postReq(url, { id: _id });
	}
	if (!res_cloud.isok) {
		// loading.close();
		// return toast.open({type:'danger', title:`Oops, something went wrong while trying to delete the device.`});
	} else {
		/** second param - reason
		 * -> we delete interface data because of
		 *  real delete request for the device,
		 *  not mode change (which is modify event for the Alexa)
		 * */
		const res_interface = await deleteAllChannelsInterfaceData(device, 'deleted');

		if (res_interface.isok) {
			// clear device status and settings from stores.
			devices_status.remove(_id);
			devices_settings.remove(_id);
			// remove virtual children if any (move to deleteAllChannelsInterfaceData())
			await deleteVirtualChildren(device);
			// remove peripheral children if any (move to deleteAllChannelsInterfaceData())
			await deletePeripheralChildren(device);
			if (isWallDisplay(device.type)) {
				await removeWallDisplayComponents(_id);
			}
			if (isXMODDevice(device.type)) {
				await removeXMODComponents(_id);
			}

			if (isCameraDevice(device)) {
				await removeCamerasFromCloudStorageSlots([device.id]);
			}

			if (isZendure) {
				try {
					let res = await shendure.get(`remove_device?device_id=${_id}`);
					if (res?.data?.isok) {
						console.log('Removed zendure device');
					}
				} catch (error) {
					return toast.open({
						type: 'danger',
						title:
							_format(`type_del_err_toast`, {
								values: { this_type: _format(`process_delete_device`) },
							}) || `Failed to delete device`,
					});
				}
			}

			toast.open({
				type: 'success',
				title:
					_format(`type_del_scc_toast`, {
						values: { this_type: _format(`process_delete_device`) },
					}) || `Device is deleted successfully.`,
			});
		} else {
			toast.open({
				type: 'danger',
				title:
					_format(`type_del_err_toast`, {
						values: { this_type: _format(`process_delete_device`) },
					}) || `Failed to delete device`,
			});
		}
		return res_cloud;
	}
}

/**
 * Remove a device that was shared *to* the current account (recipient leave).
 * Always POSTs `/device/delete_shared` with the logged-in user's email.
 * Omitting email would GET `/device/get_shared`, which is owner-only and 403s (FE-2482).
 *
 * @param id      - Device interface id
 * @param channel - Unused for the recipient path; kept for call-site compatibility
 */
export async function deleteSharedDevice(id: string, channel = 0) {
	const email = user_data.getEmail();
	return deleteSharedDeviceHouseMgmt(id, email || undefined, channel);
}

export async function deleteAllChannelsInterfaceData(device: Device, reason?: string) {
	const _id = real_id(device.id);

	let ids_to_remove = [_id];

	for (let i = 0; i < device.channels_count; i++) {
		if (i > 0) {
			ids_to_remove.push(`${_id}_${i}`);
		}
	}
	console.log('IDs to Remove', ids_to_remove);
	let data = {
		ids: JSON.stringify(ids_to_remove),
	};

	/** only on device delete from account add the reason param to php
	 * it triggers API:Integration socket event for Alexa Add/Update/Delete devices
	 */
	if (typeof reason === 'string' && reason === 'deleted') {
		data['reason'] = reason;
	}

	const res = await postReq(`/interface/device/bulk_delete`, data);

	if (res?.isok) {
		ids_to_remove.forEach((id_to_remove) => {
			devices.remove(id_to_remove);
		});
		console.log('Success, deleted device inteface data for id: ', ids_to_remove);
	} else {
		console.error('Error, Failed to delete device inteface data for ids: ', ids_to_remove);
	}
	return res;
}

export async function deleteSingleChannelInterfaceData(device: Device, reason?: string) {
	const data = {
		ids: JSON.stringify([device.id]),
	};

	if (typeof reason === 'string' && reason === 'deleted') {
		data['reason'] = reason;
	}

	const res = await postReq(`/interface/device/bulk_delete`, data);

	if (res.isok) {
		console.log('Success, deleted device inteface data for id: ', device.id);
	} else {
		console.error('Error, Failed to delete device inteface data for id: ', device.id);
	}
	return res;
}

export async function updateSingleChannelInterfaceData(device: Device) {
	const res = await postReq('interface/device/save', {
		id: device.id,
		data: JSON.stringify(device),
	});

	if (res.isok) {
		devices.update(device);
		console.log('Success, created new device inteface data for id: ', device.id);
		// custom logic for removing Shelly 2.5(gen 1) from groups when is changing it's mode
		if (device.type === 'SHSW-25') {
			const ids = getDevicesWithItsChannels(device);
			for (let group of Object.values(get(groups))) {
				let _group = group as any;
				if (ids.some((id) => _group.devices.includes(id))) {
					if (
						(device?.mode === 'roller' && ['relay', 'light'].includes(_group.type)) ||
						(device?.mode === 'relay' && _group.type === 'roller')
					) {
						const updatedIds = _group.devices.filter((id) => !ids.includes(id));
						await requestSaveGroup({ ..._group, devices: updatedIds }, _group.id);
					}
				}
			}
		}
	} else {
		console.error('Error, Failed to create new  device inteface data for id: ', device.id);
	}
	return res;
}

/**
 * Builds search IDs array for device replacement
 * @param deviceId - The base device ID
 * @param channelsCount - Number of existing channels to include in search
 * @returns Array of search IDs
 */
function buildSearchIds(deviceId: string, channelsCount: number = 1): string[] {
	const searchIds = [real_id(deviceId)];
	for (let i = 1; i < channelsCount; i++) {
		searchIds.push(`${real_id(deviceId)}_${i}`);
	}
	return searchIds;
}

/**
 * Replaces interface device data by searching for devices and replacing them with new data
 * @param deviceId - The base device ID
 * @param channelsCount - Number of existing channels to include in search
 * @param replaceData - Record mapping device IDs to their new device data objects
 */
export async function replaceInterfaceDeviceData(
	deviceId: string,
	channelsCount: number,
	replaceData: Record<string, any>,
): Promise<void> {
	const search = buildSearchIds(deviceId, channelsCount);

	Object.keys(replaceData).forEach((key) => {
		replaceData[key].cloud_options = {};
	});

	const data = {
		search: JSON.stringify(search),
		replace: JSON.stringify(replaceData),
	};

	const res = await postReq(`/interface/device/replace`, data);

	if (res?.isok) {
		Object.entries(replaceData).forEach(([deviceId, deviceData]) => {
			devices.update({ id: deviceId, ...deviceData });
		});
	} else {
		toast.open({ type: 'danger', title: 'Something went wrong' });
	}
}

export const getDeviceType = (device: Device) => {
	//this function will get device type and return it as an array. Used in background BackgroundBluetoothScan.svelte and mobile-events.ts
	let dTypeSplit;
	if ('type' in device) {
		// sometimes it is possible during inclusion that type is undefined. Observed with ios
		dTypeSplit = ['', device.type];
	} else if (typeof device.name === 'string' && device.name.includes('-')) {
		//it's possible to get device type from device name if includes '-'. But sometimes device.name appears as '' observed with Android. If it happens we will use device.id
		dTypeSplit = device.name.split('-');
	} else if (typeof device?.id === 'string' && device?.id.includes('-')) {
		//so than we can get device type from device.id.
		dTypeSplit = device.id.split('-');
	} else {
		//otherwise we will just leave it undefined because we can't get device type and maybe this isn't a known device
		dTypeSplit = undefined;
	}

	return dTypeSplit;
};
